import { BALANCE, BUILDING_DEFS, SPAWN_EDGE, colOf, inBounds } from '../constants';
import { peepName } from '../names';
import type { Simulation } from '../sim';
import type { Building, Household, Peep } from '../types';
import { serviceCapacity } from './workforce';

/** Homes with at least one free household slot (capacity-based: a townhome
 * holds 3 households, an apartment 6). */
function vacantFamilyHomes(sim: Simulation): Building[] {
  const occupants = new Map<number, number>();
  for (const household of sim.state.households) {
    occupants.set(household.homeBuildingId, (occupants.get(household.homeBuildingId) ?? 0) + 1);
  }
  return sim.state.buildings.filter((building) => {
    const capacity = BUILDING_DEFS[building.type].familyHomes ?? 0;
    return capacity > 0 && building.functional && (occupants.get(building.id) ?? 0) < capacity;
  });
}

/** Total free household slots across all functional family housing. */
export function vacantFamilyCapacity(sim: Simulation): number {
  const occupants = new Map<number, number>();
  for (const household of sim.state.households) {
    occupants.set(household.homeBuildingId, (occupants.get(household.homeBuildingId) ?? 0) + 1);
  }
  let free = 0;
  for (const building of sim.state.buildings) {
    const capacity = BUILDING_DEFS[building.type].familyHomes ?? 0;
    if (capacity > 0 && building.functional) free += Math.max(0, capacity - (occupants.get(building.id) ?? 0));
  }
  return free;
}

function serviceFunctional(sim: Simulation, service: 'education' | 'health' | 'community'): boolean {
  return sim.state.buildings.some(
    (building) => BUILDING_DEFS[building.type].service === service && building.functional,
  );
}

/** Veterans without a family who haven't been declined recently. */
function eligibleWorkers(sim: Simulation): Peep[] {
  const week = sim.week;
  return sim.state.peeps.filter(
    (peep) =>
      peep.role === 'worker' &&
      peep.tier >= 2 &&
      peep.householdId === null &&
      (peep.familyAskedWeek === null || week - peep.familyAskedWeek >= BALANCE.familyReaskWeeks),
  );
}

export function familySettlementReady(sim: Simulation): boolean {
  return serviceFunctional(sim, 'education') && serviceFunctional(sim, 'health') && vacantFamilyHomes(sim).length > 0;
}

function settleFamily(sim: Simulation, worker: Peep, home: Building, auto: boolean): void {
  const state = sim.state;
  const rng = sim.forks.families;
  const def = BUILDING_DEFS[home.type];
  const doorX = home.x + def.w / 2;
  const doorY = home.y + def.h + 0.5;

  const surname = worker.name.split(' ').slice(-1)[0] ?? 'Kin';
  const spouse = sim.spawnPeep(SPAWN_EDGE.x + 0.5, SPAWN_EDGE.y + 0.5, 'idle');
  spouse.role = 'spouse';
  spouse.job = 'idle';
  spouse.name = `${peepName(sim.forks.names).split(' ')[0]} ${surname}`;
  const kids: Peep[] = [];
  const kidCount = rng.integer(1, BALANCE.kidsPerFamilyMax);
  for (let index = 0; index < kidCount; index += 1) {
    const kid = sim.spawnPeep(SPAWN_EDGE.x + 0.5, SPAWN_EDGE.y + 0.5 + rng.range(-0.3, 0.3), 'idle');
    kid.role = 'child';
    kid.job = 'idle';
    kid.name = `${peepName(sim.forks.names).split(' ')[0]} ${surname}`;
    kid.growsUpWeek = sim.week + BALANCE.kidGrowUpWeeks + rng.integer(0, 8);
    kids.push(kid);
  }
  // Family walks toward the new home.
  for (const member of [spouse, ...kids]) {
    member.x = doorX + rng.range(-0.4, 0.4);
    member.y = doorY + rng.range(0, 0.8);
    member.px = member.x;
    member.py = member.y;
  }

  const household: Household = {
    id: state.meta.nextEntityId++,
    workerId: worker.id,
    spouseId: spouse.id,
    secondAdultId: null,
    childIds: kids.map((kid) => kid.id),
    homeBuildingId: home.id,
    happiness: 0.7,
    dust: 0,
  };
  state.households.push(household);
  worker.householdId = household.id;
  spouse.householdId = household.id;
  for (const kid of kids) kid.householdId = household.id;
  worker.tier = 3;
  sim.pushEvent({ kind: 'familySettled', workerName: worker.name, auto });
  sim.pushEvent({
    kind: 'toast',
    message: `The ${surname} family moved into their ${def.label.toLowerCase()}.`,
  });
}

/** Weekly: named requests for the first few, then automatic settlement. */
export function stepFamiliesWeekly(sim: Simulation): void {
  const state = sim.state;
  if (state.meta.gameOver) return;

  // Resolve expired requests.
  for (const request of state.familyRequests) {
    if (request.status === 'pending' && sim.day >= request.expiresDay) {
      request.status = 'expired';
      const worker = state.peeps.find((peep) => peep.id === request.peepId);
      if (worker) worker.familyAskedWeek = sim.week;
      state.workforce.morale = Math.max(0, state.workforce.morale - BALANCE.familyDeclineMoraleHit);
      sim.pushEvent({ kind: 'toast', message: `${request.workerName}'s family request went unanswered.` });
    }
  }

  if (!familySettlementReady(sim)) return;
  const eligible = eligibleWorkers(sim);
  if (eligible.length === 0) return;

  const letterPhase = state.workforce.familyRequestsSent < BALANCE.familyLetterCount;
  if (letterPhase) {
    if (state.familyRequests.some((request) => request.status === 'pending')) return;
    const worker = eligible[0]!;
    const request = {
      id: state.meta.nextEntityId++,
      peepId: worker.id,
      workerName: worker.name,
      day: sim.day,
      expiresDay: sim.day + BALANCE.familyRequestExpiryDays,
      status: 'pending' as const,
    };
    state.familyRequests.push(request);
    state.workforce.familyRequestsSent += 1;
    worker.familyAskedWeek = sim.week;
    sim.pushEvent({ kind: 'familyRequest', requestId: request.id, workerName: worker.name });
    return;
  }

  // Automatic phase: bigger towns settle faster, bounded by vacancy.
  let settled = 0;
  const cap = familySettlementCap(state.households.length);
  for (const worker of eligible) {
    if (settled >= cap) break;
    const homes = vacantFamilyHomes(sim);
    if (homes.length === 0) break;
    settleFamily(sim, worker, homes[0]!, true);
    settled += 1;
  }
}

/** Auto settlements allowed this week — grows with the town, so reaching
 * hundreds of residents doesn't take a fixed 1/week forever. */
export function familySettlementCap(households: number): number {
  return Math.min(BALANCE.familyAutoMax, 1 + Math.floor(households / BALANCE.familyAutoPerHouseholds));
}

/** A resident household drawn by the town itself: two adults (both employable
 * in services), maybe kids, no mine worker. Arrives at the rail depot when
 * one exists — stepping off the train into your town. */
export function settleResidentFamily(sim: Simulation): void {
  const state = sim.state;
  const rng = sim.forks.families;
  const homes = vacantFamilyHomes(sim);
  const home = homes[0];
  if (!home) return;
  const def = BUILDING_DEFS[home.type];
  const depot = state.buildings.find((building) => building.type === 'railDepot' && building.functional);
  const arriveX = depot ? depot.x + 1.5 : SPAWN_EDGE.x + 0.5;
  const arriveY = depot ? depot.y + BUILDING_DEFS.railDepot.h + 0.5 : SPAWN_EDGE.y + 0.5;

  const surname = peepName(sim.forks.names).split(' ').slice(-1)[0] ?? 'Newcomer';
  const adultA = sim.spawnPeep(arriveX, arriveY, 'idle');
  adultA.role = 'spouse';
  adultA.name = `${peepName(sim.forks.names).split(' ')[0]} ${surname}`;
  const adultB = sim.spawnPeep(arriveX, arriveY + 0.4, 'idle');
  adultB.role = 'spouse';
  adultB.name = `${peepName(sim.forks.names).split(' ')[0]} ${surname}`;
  const kids: Peep[] = [];
  const kidCount = rng.integer(0, BALANCE.kidsPerFamilyMax);
  for (let index = 0; index < kidCount; index += 1) {
    const kid = sim.spawnPeep(arriveX + rng.range(-0.3, 0.3), arriveY + 0.8, 'idle');
    kid.role = 'child';
    kid.name = `${peepName(sim.forks.names).split(' ')[0]} ${surname}`;
    kid.growsUpWeek = sim.week + BALANCE.kidGrowUpWeeks + rng.integer(0, 8);
    kids.push(kid);
  }
  const doorX = home.x + def.w / 2;
  const doorY = home.y + def.h + 0.5;
  for (const member of [adultA, adultB, ...kids]) {
    member.x = doorX + rng.range(-0.5, 0.5);
    member.y = doorY + rng.range(0, 0.8);
    member.px = member.x;
    member.py = member.y;
  }
  const household: Household = {
    id: state.meta.nextEntityId++,
    workerId: null,
    spouseId: adultA.id,
    secondAdultId: adultB.id,
    childIds: kids.map((kid) => kid.id),
    homeBuildingId: home.id,
    happiness: 0.7,
    dust: 0,
  };
  state.households.push(household);
  adultA.householdId = household.id;
  adultB.householdId = household.id;
  for (const kid of kids) kid.householdId = household.id;
  sim.pushEvent({ kind: 'residentArrived', surname });
  sim.pushEvent({ kind: 'toast', message: `The ${surname} family moved to town — no mine required.` });
}

export function resolveFamilyRequest(sim: Simulation, requestId: number, approve: boolean): void {
  const state = sim.state;
  const request = state.familyRequests.find((candidate) => candidate.id === requestId);
  if (!request || request.status !== 'pending') return;
  const worker = state.peeps.find((peep) => peep.id === request.peepId);
  if (!worker) {
    request.status = 'expired';
    return;
  }
  if (!approve) {
    request.status = 'declined';
    worker.familyAskedWeek = sim.week;
    state.workforce.morale = Math.max(0, state.workforce.morale - BALANCE.familyDeclineMoraleHit);
    sim.pushEvent({ kind: 'toast', message: `${worker.name} takes the news quietly.` });
    return;
  }
  const homes = vacantFamilyHomes(sim);
  if (homes.length === 0 || !familySettlementReady(sim)) {
    sim.pushEvent({ kind: 'toast', message: 'No vacant family home — build a cottage first.' });
    return;
  }
  request.status = 'approved';
  settleFamily(sim, worker, homes[0]!, false);
}

/** Daily: dust + services roll into household happiness and family needs. */
export function stepFamiliesDaily(sim: Simulation): void {
  const state = sim.state;
  const needs = state.workforce.familyNeeds;
  if (state.households.length === 0) {
    needs.education = 1;
    needs.health = 1;
    needs.community = 1;
    needs.air = 1;
    return;
  }

  // Needs are capacity ratios: one school serves ~100 kids, not any-size town.
  const kids = state.peeps.filter((peep) => peep.role === 'child').length;
  const everyone = state.peeps.length;
  const ratio = (kind: 'education' | 'health' | 'community', demand: number): number =>
    demand === 0 ? 1 : Math.min(1, serviceCapacity(state, kind) / demand);
  needs.education = ratio('education', kids);
  needs.health = ratio('health', everyone);
  needs.community = ratio('community', everyone);

  let airTotal = 0;
  // Households share buildings (apartments hold six) — resolve homes by id
  // once and compute each building's dust scan once, not per household.
  const buildingsById = new Map(state.buildings.map((building) => [building.id, building]));
  const dustByBuilding = new Map<number, number>();
  for (const household of state.households) {
    const home = buildingsById.get(household.homeBuildingId);
    if (!home) continue;
    let dust = dustByBuilding.get(home.id);
    if (dust === undefined) {
      dust = homeDustLevel(sim, home.x, home.y);
      dustByBuilding.set(home.id, dust);
    }
    household.dust = dust;
    const def = BUILDING_DEFS[home.type];
    const air = 1 - household.dust;
    airTotal += air;
    household.happiness = Math.max(
      0,
      Math.min(
        1,
        0.25 * needs.education +
          0.2 * needs.health +
          0.15 * needs.community +
          0.4 * air +
          (def.homeComfort ?? 0),
      ),
    );
  }
  needs.air = airTotal / state.households.length;

  // Kids come of age.
  for (const peep of state.peeps) {
    if (peep.role !== 'child' || peep.growsUpWeek === null) continue;
    if (sim.week >= peep.growsUpWeek) {
      const educated = state.buildings.some(
        (building) => building.type === 'university' && building.functional,
      );
      peep.role = 'worker';
      peep.job = 'idle';
      peep.tier = educated ? 1 : 0;
      peep.tenureWeeks = educated ? 2 : 0;
      peep.growsUpWeek = null;
      sim.kidsGrownUp += 1;
      sim.pushEvent({ kind: 'kidGrewUp', name: peep.name });
      sim.pushEvent({
        kind: 'toast',
        message: educated
          ? `${peep.name} graduated and joins the workforce as a Regular.`
          : `Young ${peep.name} starts at the mine Monday.`,
      });
    }
  }
}

/** Dust at a building site: excavation or dig orders nearby. Exported for the
 * placement ghost hint (accepts any sim-state holder, including SimView). */
export function homeDustLevel(
  sim: { state: { terrain: import('../types').Terrain; orders: ReadonlyMap<number, unknown> } },
  x: number,
  y: number,
): number {
  const terrain = sim.state.terrain;
  let dusty = 0;
  for (let dy = -BALANCE.dustRadius; dy <= BALANCE.dustRadius; dy += 1) {
    for (let dx = -BALANCE.dustRadius; dx <= BALANCE.dustRadius; dx += 1) {
      const tx = x + dx;
      const ty = y + dy;
      if (!inBounds(tx, ty)) continue;
      const col = colOf(tx, ty);
      if ((terrain.depth[col] ?? 0) > 0 || sim.state.orders.has(col)) dusty += 1;
    }
  }
  return Math.min(1, dusty / BALANCE.dustTilesForMax);
}
